import { connect } from 'react-redux';
import {
  groupBy,
  sortBy,
  flatten,
  prop,
  filter,
  apply,
  lt,
  keys,
  map,
} from 'ramda';
import { createSelector } from 'reselect';
import getYear from 'date-fns/get_year';

import { getCityNameWithState } from '../utils/format';
import { legendColors } from '../theme';
import {
  getSortedSelectedWithDataIds,
  getLocations,
} from '../selectors/locations';
import { getCrimes } from '../selectors/crimes';
import { getMetric } from '../selectors/options';
import { MetricProp, VisType } from '../constants';
import GenericBarChart, { placeGroupedData } from './GenericBarChart';
import ChartAndLegend from './ChartAndLegend';

export const getLegendData = createSelector(
  getLocations,
  getSortedSelectedWithDataIds,
  (locations, ids) =>
    ids.map((id, i) => {
      const location = locations.find(location => location.id === id);
      return {
        id,
        name: location.name,
        state: location.state,
        color: legendColors[i % legendColors.length],
      };
    }),
);

export const getRawData = createSelector(
  getLegendData,
  getCrimes,
  getMetric,
  (legend, crimes, metric) =>
    legend.map(({ id, name, state, color }) =>
      crimes[id].map(d => ({
        id,
        name,
        state,
        fill: color,
        year: getYear(d.timespan.split('/')[0]),
        value: d[MetricProp[metric]],
      })),
    ),
);

export const setYFromValues = flattenedData => {
  const minValue = Math.min(
    1,
    apply(
      Math.min,
      filter(lt(0), flattenedData.map(({ value }) => Math.abs(value))),
    ) / 4,
  );

  return flattenedData.map(entry => ({
    ...entry,
    y: entry.value === 0 ? minValue : entry.value,
  }));
};

export const getGroupedData = createSelector(getRawData, data =>
  groupBy(prop('name'), setYFromValues(flatten(data))),
);

export const barWidth = 16;
export const barSpacing = 4;
export const yearSpacing = 33;

export const getSortedYears = createSelector(getGroupedData, data =>
  keys(data).sort(),
);

export const getPlacedData = createSelector(
  getGroupedData,
  getSortedYears,
  (data, locations) =>
    placeGroupedData(
      locations.map(location => sortBy(prop('year'), data[location])),
      barWidth,
      barSpacing,
      yearSpacing,
    ),
);

export const getChartData = createSelector(getPlacedData, flatten);

export const getXTickValues = createSelector(
  getPlacedData,
  map(group => group[0].x - barWidth / 2),
);

export default connect(state => ({
  chartComponent: GenericBarChart,
  chartData: getChartData(state),
  legendData: getLegendData(state),
  resetSelectionOnClick: true,
  xTickLabels: getSortedYears(state),
  xTickValues: getXTickValues(state),
  showVerticalGrid: true,
  labelAnchor: 'start',
  hoverHPadding: barSpacing / 2,
  onMouseOver: (
    { id, value, year, fill, name, state },
    { setHovered },
    [offsetX, offsetY],
  ) => {
    setHovered(id, null, null, {
      year,
      x: offsetX,
      y: offsetY,
      visType: VisType.RECURRING,
      mainValue: value,
      sideColor: fill,
      title: getCityNameWithState(name, state),
    });
  },
  onMouseOut: ({ id }, { setHovered }) => {
    setHovered(null, null, id, null);
  },
  onClick: ({ id }, { setSelected }) => {
    setSelected(id);
  },
}))(ChartAndLegend);



// WEBPACK FOOTER //
// ./src/components/RecurringChartContainer.jsx